import { useSyncExternalStore } from "react";

import { readDexSdk } from "@/contexts/dex-sdk";
import { isProduction } from "@/env";

// Maximum allowed delta between server and client time, in milliseconds.
const MAX_DELTA = 24 * 60 * 60 * 1000;

// The difference between the server time and the client time, in milliseconds.
let serverTimeDelta: number | undefined;
let syncInitiated = false;
let syncPromise: Promise<void> | undefined;

// Callbacks to notify when server time delta changes
const serverTimeDeltaCallbacks = new Set<(delta: number) => void>();

// Callbacks to notify when synchronizeTime fails (so the UI can warn the user
// their clock may be off). Fired from inside the catch in synchronizeTime.
const serverTimeSyncErrorCallbacks = new Set<(error: unknown) => void>();

async function getServerTimeDelta() {
  // The request cannot be cached (otherwise we would receive a stale timestamp).
  const requestTime = Date.now();

  // Call Aptos node's timestamp view function using the SDK
  const result = await readDexSdk.deps.aptos.view({
    payload: {
      function: "0x1::timestamp::now_microseconds",
      typeArguments: [],
      functionArguments: [],
    },
  });

  const responseTime = Date.now();

  // The response is an array with a single value representing microseconds
  if (!Array.isArray(result) || result.length !== 1) {
    throw new Error("Unexpected response format from timestamp view function");
  }

  const serverTimeMicroseconds = BigInt(result[0] as string | number);
  const serverTime = Number(serverTimeMicroseconds / BigInt(1000)); // Convert to milliseconds

  // Assume that the server generated its time halfway between request sent
  // and response received.
  const clientTime = (requestTime + responseTime) / 2;
  const newDelta = serverTime - clientTime;

  if (Number.isNaN(newDelta)) {
    throw new Error("newDelta is NaN");
  } else if (Math.abs(newDelta) > MAX_DELTA) {
    throw new Error("MAX_DELTA exceeded");
  }

  return Math.round(newDelta);
}

/**
 * Synchronize local time with server time, storing the delta in a module
 * variable. Catches errors so unrelated callers can't crash on a timestamp
 * RPC blip; emits to `onServerTimeSyncError` subscribers so the UI can warn
 * the user their clock may be off. Rethrows in dev so the failure is loud.
 */
export function synchronizeTime(): Promise<void> {
  if (serverTimeDelta !== undefined) return Promise.resolve();
  syncPromise ??= performSync();
  return syncPromise;
}

async function performSync() {
  syncInitiated = true;
  try {
    const delta = await getServerTimeDelta();
    serverTimeDelta = delta;
    serverTimeDeltaCallbacks.forEach((callback) => callback(delta));
  } catch (error) {
    syncPromise = undefined;
    serverTimeSyncErrorCallbacks.forEach((callback) => callback(error));
    if (!isProduction) {
      throw error;
    }
  }
}

/**
 * Returns the current server time in milliseconds.
 *
 * A previous call to {@link synchronizeTime} will ensure the time is
 * synchronized with the server.
 */
export function getServerTime() {
  if (!isProduction && !syncInitiated) {
    console.warn("Server time not synchronized. Call synchronizeTime() first.");
  }
  return Date.now() + (serverTimeDelta ?? 0);
}

/**
 * Get a `Date` object for the current server time
 */
export function getServerDate() {
  return new Date(getServerTime());
}

/**
 * Get the current server time delta in milliseconds.
 * Returns 0 if time hasn't been synchronized yet.
 */
export function getServerTimeDeltaSync(): number {
  return serverTimeDelta ?? 0;
}

function subscribeToServerTimeDelta(onStoreChange: () => void): () => void {
  return onServerTimeDeltaChange(() => onStoreChange());
}

/** Subscribe to server-time delta changes. Call in hooks that use getServerTime() during render. */
export function useServerTimeDelta(): number {
  return useSyncExternalStore(
    subscribeToServerTimeDelta,
    getServerTimeDeltaSync,
    getServerTimeDeltaSync,
  );
}

/**
 * Default expiry duration (in seconds) for regular DEX transactions.
 *
 * Replaces the Aptos SDK's `getDefaultTxnExpirySecFromNow()` (~20s), which is
 * too short for MPC wallets (e.g. Fordefi) whose distributed signing process
 * can take long enough for the transaction to expire before submission. We use
 * 60s as a safe middle ground — long enough for MPC quorum signing and any
 * user-prompt latency, short enough that an abandoned tx clears quickly.
 */
export const DEFAULT_TXN_EXPIRY_SECONDS = 60;

/**
 * Extended expiry duration (in seconds) for cross-chain transaction flows.
 *
 * Cross-chain accounts prep transactions for bridging and other x-chain actions,
 * but the default Aptos transaction expiry (~20s) is too short for these multi-step
 * flows. We set this as high as possible to account for bridge latency, user signing
 * delays, and network congestion on source/destination chains.
 *
 * Only used by x-chain mutations (CCTP claim and the cross-chain-core library patch).
 * Regular DEX transactions keep the Aptos default.
 *
 * Why 115s: Aptos mainnet enforces a hard maximum transaction lifetime of 120 seconds.
 * The expiration timestamp is computed from our server-synced clock (`getServerTime()`),
 * which may drift slightly from the Aptos validator's clock. We use 115s (5s below the
 * limit) to leave headroom for this clock drift and any processing delay between
 * timestamp computation and the validator receiving the transaction.
 */
export const XCHAIN_EXPIRY_SECONDS = 115;

/**
 * Get an expiration timestamp in seconds for Aptos transactions.
 * Uses server time and adds the default transaction expiry duration.
 * @param defaultExpirySeconds - Default expiry duration in seconds (from AptosConfig)
 * @returns Expiration timestamp in seconds
 */
export function getExpirationTimestamp(defaultExpirySeconds: number): number {
  return Math.floor(getServerTime() / 1000) + defaultExpirySeconds;
}

/**
 * Subscribe to server time delta changes.
 * The callback will be called whenever the server time delta is updated.
 * @param callback - Function to call when server time delta changes
 * @returns Unsubscribe function
 */
export function onServerTimeDeltaChange(callback: (delta: number) => void): () => void {
  serverTimeDeltaCallbacks.add(callback);
  return () => {
    serverTimeDeltaCallbacks.delete(callback);
  };
}

/**
 * Subscribe to synchronizeTime() failures. Fires from inside its catch block
 * before the in-prod swallow, so the UI can surface a warning to the user.
 * @param callback - Function to call when synchronizeTime throws
 * @returns Unsubscribe function
 */
export function onServerTimeSyncError(callback: (error: unknown) => void): () => void {
  serverTimeSyncErrorCallbacks.add(callback);
  return () => {
    serverTimeSyncErrorCallbacks.delete(callback);
  };
}

// Errors swallowed -- ServerTimeSync's useEffect retries with subscribers wired.
if (typeof window !== "undefined") {
  void synchronizeTime().catch(Function.prototype as () => void);
}
